import React, { ReactNode, useCallback, useMemo } from "react";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { faCopy } from "@fortawesome/pro-solid-svg-icons/faCopy";
import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notification } from "antd";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import RoundButton from "@app/component/RoundButton";
import useClickTracker from "@app/util/contexts/useClickTracker";
import { parsePhoneNumber } from "@app/util/parsers";

interface Props {
  disabled?: boolean;
  onClick?: () => void;
  phoneNumber?: null | string;
  tooltip?: ReactNode;
  trackKey?: string;
}

const messages = defineMessages({
  call: {
    id: "actions.call",
    defaultMessage: "Call",
    description: "Label of the Call action",
  },
  callFromDevice: {
    id: "actions.call.callFromDevice",
    defaultMessage: "Call from device",
    description: "Label to call",
  },
  copyToClipboard: {
    id: "actions.call.copyToClipboard",
    defaultMessage: "Copy to clipboard: {phoneNumber} ",
    description: "Copies number to device clipboard",
  },
  phoneNumberCopied: {
    id: "actions.call.phoneNumberCopied",
    defaultMessage: "Phone number copied to clipboard",
    description: "Phone number copied to clipboard ",
  },
});

const Call: React.FC<Props> = ({ disabled, onClick, phoneNumber, tooltip, trackKey }) => {
  const intl = useIntl();

  const parsedPhoneNumber = parsePhoneNumber(phoneNumber ?? undefined);
  const isDisabled = disabled || !parsedPhoneNumber?.link;
  const formattedPhoneNumber = parsedPhoneNumber?.link.replace("tel:", "");

  const handleClick = useClickTracker({
    disabled: isDisabled,
    onClick,
    trackKey,
  });

  const handleCopyToClipboard = useCallback(async () => {
    if (formattedPhoneNumber) {
      await navigator.clipboard.writeText(formattedPhoneNumber);
      notification.success({
        message: intl.formatMessage(messages.phoneNumberCopied),
      });
    }
  }, [formattedPhoneNumber, intl]);

  const handleCallFromDevice = useCallback(() => {
    if (parsedPhoneNumber?.link) {
      window.open(parsedPhoneNumber.link, "_blank");
    }
  }, [parsedPhoneNumber]);

  const menu: MenuProps = useMemo(
    () => ({
      items: [
        {
          icon: <FontAwesomeIcon icon={faCopy} />,
          key: "copyToClipboard",
          label: intl.formatMessage(messages.copyToClipboard, {
            phoneNumber: formattedPhoneNumber,
          }),
          onClick: handleCopyToClipboard,
        },
        {
          icon: <FontAwesomeIcon icon={faPhone} />,
          key: "callFromDevice",
          label: intl.formatMessage(messages.callFromDevice),
          onClick: handleCallFromDevice,
        },
      ],
    }),
    [formattedPhoneNumber, handleCallFromDevice, handleCopyToClipboard, intl]
  );

  const getParentElement = useCallback((trigger: HTMLElement) => trigger.parentElement!, []);

  return (
    <Tooltip placement="bottom" title={tooltip} trigger={tooltip ? ["hover"] : []}>
      <div>
        <Dropdown
          disabled={isDisabled}
          getPopupContainer={getParentElement}
          menu={menu}
          trigger={["click"]}
        >
          <RoundButton
            disabled={isDisabled}
            leftIcon={<FontAwesomeIcon icon={faPhone} />}
            onClick={handleClick}
            rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
          >
            {intl.formatMessage(messages.call)}
          </RoundButton>
        </Dropdown>
      </div>
    </Tooltip>
  );
};

export default Call;
